import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Input,
  Col,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { weighbillItemValidationSchema } from "utils";
import { UpdateWeighbillItemPayload, WeighbillItem } from "types";
import { toast } from "react-toastify";
import { useGetProducts } from "hooks/useProduct";

export const AddWeighBillItemModal = ({
  isOpen,
  toggle,
  newWeighBillItem,
  editDetails,
  isEdit,
  setIsEdit,
  itemIndex,
}: {
  isOpen: boolean;
  toggle: () => void;
  newWeighBillItem: Dispatch<SetStateAction<any[]>>;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  editDetails?: UpdateWeighbillItemPayload;
  isEdit: boolean;
  itemIndex: number;
}) => {
  const { data: productData } = useGetProducts();
  const [title, setTitle] = useState("Add WeighBill Item");
  const initialValues = {
    product_id: 0,
    cost_price: 0,
    quantity: 0,
    discount_percentage: 0,
    round_up: 0,
    weight: 0,
  };

  const formik = useFormik<Partial<WeighbillItem>>({
    initialValues: initialValues,
    validationSchema: weighbillItemValidationSchema,
    onSubmit: async (values) => {
      const discountAmount =
        (((values.cost_price ?? 0) * (values.discount_percentage ?? 0)) / 100) *
        (values.quantity ?? 0);

      const payload = {
        ...values,
        discount_amt: discountAmount,
      };
      if (isEdit) {
        toast.success("WeighBill item updated.");
        newWeighBillItem((prevItems) => {
          const updatedItems = [...prevItems];
          updatedItems[itemIndex] = payload;
          return updatedItems;
        });
        setIsEdit(false);
      } else {
        toast.success("WeighBill item added.");
        newWeighBillItem((prevItems) => [...prevItems, payload]);
      }
      setTitle("Add WeighBill Item");
      toggle();
      formik.resetForm();
    },
  });

  const handleCancelClick = () => {
    formik.resetForm();
    setTitle("Add WeighBill Item");
    setIsEdit(false);
    toggle();
  };

  useEffect(() => {
    if (isEdit && editDetails) {
      formik.setValues({
        product_id: editDetails.product?.id || editDetails?.product_id,
        product_name: editDetails.product?.name,
        cost_price: editDetails.cost_price,
        quantity: editDetails.quantity,
        discount_percentage: editDetails.discount_percentage,
        round_up: editDetails.round_up,
        weight: editDetails.weight,
      });
      setTitle("Edit WeighBill Item");
    }
  }, [editDetails, isEdit]);

  return (
    <Modal isOpen={isOpen} toggle={handleCancelClick} centered>
      <ModalHeader toggle={handleCancelClick}>{title}</ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            return false;
          }}
        >
          <Row>
            <Col sm="12">
              <div className="mb-3">
                <Label htmlFor="product_id">Product</Label>
                <Input
                  type="select"
                  name="product_id"
                  onChange={(e) => {
                    const selectedProduct = productData.find(
                      (product) => product.id === Number(e.target.value)
                    );
                    formik.setFieldValue("product_id", e.target.value);
                    formik.setFieldValue("product_name", selectedProduct.name);
                  }}
                  value={formik.values.product_id}
                  invalid={
                    !!(formik.touched.product_id && formik.errors.product_id)
                  }
                >
                  <option value="">Select Product...</option>
                  {productData &&
                    productData.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                </Input>
                {formik.touched.product_id && formik.errors.product_id && (
                  <span className="text-danger">
                    {String(formik.errors.product_id)}
                  </span>
                )}
              </div>
            </Col>

            {/* cost price */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="cost_price">Cost Price</Label>
                <Input
                  type="number"
                  name="cost_price"
                  min="0"
                  onChange={(e) =>
                    formik.setFieldValue("cost_price", e.target.value)
                  }
                  value={formik.values.cost_price}
                  invalid={
                    !!(formik.touched.cost_price && formik.errors.cost_price)
                  }
                />
                {formik.touched.cost_price && formik.errors.cost_price && (
                  <span className="text-danger">
                    {formik.errors.cost_price}
                  </span>
                )}
              </div>
            </Col>

            {/* quantity */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  type="number"
                  name="quantity"
                  min="0"
                  onChange={(e) =>
                    formik.setFieldValue("quantity", e.target.value)
                  }
                  value={formik.values.quantity}
                  invalid={
                    !!(formik.touched.quantity && formik.errors.quantity)
                  }
                />
                {formik.touched.quantity && formik.errors.quantity && (
                  <span className="text-danger">{formik.errors.quantity}</span>
                )}
              </div>
            </Col>

            {/* dicount percentage */}
            <Col sm="12">
              <div className="mb-3">
                <Label htmlFor="discount_percentage">Discount Percentage</Label>
                <Input
                  type="number"
                  name="discount_percentage"
                  min="0"
                  onChange={(e) =>
                    formik.setFieldValue("discount_percentage", e.target.value)
                  }
                  value={formik.values.discount_percentage}
                  invalid={
                    !!(
                      formik.touched.discount_percentage &&
                      formik.errors.discount_percentage
                    )
                  }
                />
                {formik.touched.discount_percentage &&
                  formik.errors.discount_percentage && (
                    <span className="text-danger">
                      {formik.errors.discount_percentage}
                    </span>
                  )}
              </div>
            </Col>

            {/* round up */}
            <Col sm="12">
              <div className="mb-3">
                <Label htmlFor="round_up">Round Up</Label>
                <Input
                  type="number"
                  min="0"
                  name="round_up"
                  onChange={(e) =>
                    formik.setFieldValue("round_up", e.target.value)
                  }
                  value={formik.values.round_up}
                  invalid={
                    !!(formik.touched.round_up && formik.errors.round_up)
                  }
                />
                {formik.touched.round_up && formik.errors.round_up && (
                  <span className="text-danger">{formik.errors.round_up}</span>
                )}
              </div>
            </Col>

            {/* weight */}
            <Col sm="12">
              <div className="mb-3">
                <Label htmlFor="weight">Weight</Label>
                <Input
                  type="number"
                  name="weight"
                  min="0"
                  onChange={(e) => {
                    formik.setFieldValue("weight", e.target.value);
                  }}
                  value={formik.values.weight}
                  invalid={!!(formik.touched.weight && formik.errors.weight)}
                />
                {formik.touched.weight && formik.errors.weight && (
                  <span className="text-danger">{formik.errors.weight}</span>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          color="success"
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          Submit
        </Button>
        <Button color="secondary" onClick={() => handleCancelClick()}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
